import { useEffect, useRef, useState } from 'react';
import { setMeta } from '@simplenotation/core';
import { useStore, edit, openModal, setState, unsaved, undo, redo } from '../state/store';
import { canRedo, canUndo } from '../state/history';
import { BUG_ICON, runCommand } from '../commands/registry';
import { save, setPrivacy, openSearch, saveDraft } from '../app/actions';
import { Icon } from './common';
import { commandTip, shortcutFor } from './CommandButton';
import { tip } from './Tooltip';

function Menu({ label, children, className = '' }: { label: React.ReactNode; children: React.ReactNode; className?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', close);
    return () => window.removeEventListener('mousedown', close);
  }, [open]);
  return (
    <div className={`menu ${className}`} ref={ref}>
      <button className="topbtn" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {label}
      </button>
      {open && (
        <div className="menu-pop" role="menu" onClick={() => setOpen(false)}>
          {children}
        </div>
      )}
    </div>
  );
}

function MenuItem({ cmd, children, onClick, disabled, keys }: { cmd?: string; children: React.ReactNode; onClick?: () => void; disabled?: boolean; keys?: string }) {
  return (
    <button role="menuitem" className="menu-item" disabled={disabled} onClick={() => (onClick ? onClick() : cmd && runCommand(cmd))}>
      {children}
      {keys && <span className="menu-keys">{keys}</span>}
    </button>
  );
}

/**
 * Undo and redo, always visible while the score can be edited. The tooltip
 * names the step ("Undo Place note"); the buttons are greyed out when there is
 * nothing to undo or redo.
 */
function UndoRedo() {
  const history = useStore((s) => s.history);
  const mode = useStore((s) => s.mode);
  const live = mode === 'edit';
  const back = live && canUndo(history);
  const fwd = live && canRedo(history);
  const undoLabel = back ? `Undo ${history.entries[history.index].label}` : 'Nothing to undo';
  const redoLabel = fwd ? `Redo ${history.entries[history.index + 1].label}` : 'Nothing to redo';
  return (
    <div className="undo-redo" role="group" aria-label="Undo and redo">
      <button type="button" className="topbtn icon" aria-label={undoLabel} disabled={!back} onClick={() => undo()} data-testid="undo" {...tip(undoLabel, { keys: shortcutFor('edit.undo'), side: 'bottom' })}>
        <Icon icon={{ svg: 'M9 7H4V2M4.5 7A8 8 0 1 1 4 15' }} size={18} />
      </button>
      <button type="button" className="topbtn icon" aria-label={redoLabel} disabled={!fwd} onClick={() => redo()} data-testid="redo" {...tip(redoLabel, { keys: shortcutFor('edit.redo'), side: 'bottom' })}>
        <Icon icon={{ svg: 'M15 7h5V2m-.5 5A8 8 0 1 0 20 15' }} size={18} />
      </button>
    </div>
  );
}

export function TopBar() {
  const title = useStore((s) => s.ed.score.meta.title);
  const doc = useStore((s) => s.doc);
  const mode = useStore((s) => s.mode);
  const user = useStore((s) => s.user);
  const history = useStore((s) => s.history);
  const [editing, setEditing] = useState(false);
  const count = unsaved();
  const clean = count === 0 && !!doc.slug;

  return (
    <header className="topbar">
      <div className="topbar-left">
        <a
          href="/"
          className="brand"
          onClick={() => saveDraft()}
          aria-label="SimpleNotation home"
        >
          <svg viewBox="0 0 32 32" width="24" height="24" aria-hidden="true">
            <rect x="2" y="2" width="28" height="28" rx="7" fill="#ef6a4c" />
            <path d="M8 10h16M8 14h16M8 18h16M8 22h16" stroke="#fff" strokeWidth="1.4" opacity=".75" />
            <circle cx="13" cy="18" r="3" fill="#fff" />
            <path d="M15.6 18V8.5c2 .8 3.6 2 4.4 3.6" stroke="#fff" strokeWidth="1.6" fill="none" strokeLinecap="round" />
          </svg>
          <span>Library</span>
        </a>
        <span className="crumb-sep">/</span>
        {editing && doc.canEdit ? (
          <input
            className="title-input"
            autoFocus
            defaultValue={title}
            aria-label="Score title"
            onBlur={(e) => {
              const v = e.target.value.trim() || 'Untitled';
              if (v !== title) edit((st) => setMeta(st, { title: v }), { label: 'Rename' });
              setEditing(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              if (e.key === 'Escape') setEditing(false);
            }}
          />
        ) : (
          <button className="title" title={doc.canEdit ? 'Rename' : undefined} onClick={() => doc.canEdit && setEditing(true)}>
            {title || 'Untitled'}
          </button>
        )}
        {doc.canEdit ? (
          <div className="mode-toggle" role="radiogroup" aria-label="Mode" {...commandTip('app.toggleMode', { hint: 'Edit changes the notation. View is for reading and playing it, with view-only transposition.', side: 'bottom' })}>
            <button role="radio" aria-checked={mode === 'edit'} className={mode === 'edit' ? 'on' : ''} onClick={() => mode !== 'edit' && runCommand('app.toggleMode')}>
              Edit
            </button>
            <button role="radio" aria-checked={mode === 'view'} className={mode === 'view' ? 'on' : ''} onClick={() => mode !== 'view' && runCommand('app.toggleMode')}>
              View
            </button>
          </div>
        ) : (
          <span className="view-only">View only</span>
        )}
        {doc.canEdit && <UndoRedo />}
        {doc.canEdit && (
          <span className={`doc-status${count > 0 ? ' dirty' : ''}`} data-testid="unsaved" role="status">
            {doc.status === 'saving' ? 'Saving…' : count === 0 ? (doc.slug ? 'All changes saved' : 'Not saved yet') : `${count} unsaved change${count === 1 ? '' : 's'}`}
          </span>
        )}
        {!doc.canEdit && doc.ownerName && <span className="owner">by {doc.ownerName}</span>}
      </div>
      <div className="topbar-right">
        <Menu label={<span aria-label="More">⋯</span>}>
          {doc.canEdit && (
            <>
              <MenuItem onClick={() => undo()} disabled={mode !== 'edit' || !canUndo(history)} keys={shortcutFor('edit.undo')}>
                {mode === 'edit' && canUndo(history) ? `Undo ${history.entries[history.index].label}` : 'Undo'}
              </MenuItem>
              <MenuItem onClick={() => redo()} disabled={mode !== 'edit' || !canRedo(history)} keys={shortcutFor('edit.redo')}>
                {mode === 'edit' && canRedo(history) ? `Redo ${history.entries[history.index + 1].label}` : 'Redo'}
              </MenuItem>
              <div className="menu-sep" />
            </>
          )}
          <MenuItem cmd="app.new">New score</MenuItem>
          <MenuItem cmd="app.import">Import notation file…</MenuItem>
          <MenuItem cmd="app.importSlice">Import another score as instruments…</MenuItem>
          <div className="menu-sep" />
          <MenuItem cmd="app.details">Edit details…</MenuItem>
          <MenuItem cmd="app.duplicate">Duplicate…</MenuItem>
          <MenuItem cmd="app.versions">Versions…</MenuItem>
          <div className="menu-sep" />
          <MenuItem cmd="app.reportBug">Report a bug…</MenuItem>
        </Menu>
        <Menu
          label={
            <span aria-label="Export" className="export-label">
              <span className="narrow-only">
                <Icon icon={{ svg: 'M12 4v11M7 10l5 5 5-5M5 20h14' }} size={16} />
              </span>
              <span className="wide-only">Export</span> ▾
            </span>
          }
          className="export-menu"
        >
          <MenuItem cmd="app.exportPdf">PDF</MenuItem>
          <MenuItem cmd="app.exportPng">PNG image</MenuItem>
          <MenuItem cmd="app.exportGuitarPro">Guitar Pro (.gp)</MenuItem>
          <MenuItem cmd="app.exportTextTab">Text tab (.txt)</MenuItem>
          <MenuItem cmd="app.copyTextTab">Copy as text tab</MenuItem>
          <MenuItem cmd="app.exportMusicXml">MusicXML</MenuItem>
          <MenuItem cmd="app.exportMidi">MIDI</MenuItem>
          <MenuItem cmd="app.exportMp3">MP3 audio</MenuItem>
          <MenuItem cmd="app.exportWav">WAV audio</MenuItem>
        </Menu>
        <Menu label="Help" className="help-menu">
          <MenuItem cmd="app.tour">Show the tour</MenuItem>
          <MenuItem cmd="app.shortcuts">Keyboard shortcuts</MenuItem>
          <MenuItem onClick={() => openSearch()}>Search commands</MenuItem>
          <div className="menu-sep" />
          <MenuItem cmd="app.reportBug">Report a bug…</MenuItem>
          <MenuItem onClick={() => window.open('/contact', '_blank', 'noopener')}>Contact us</MenuItem>
        </Menu>
        <button type="button" className="topbtn bug-btn" onClick={() => runCommand('app.reportBug')} data-testid="report-bug" {...tip('Found a bug? Report it', { side: 'bottom' })}>
          <Icon icon={{ svg: BUG_ICON }} size={16} />
          <span className="wide-only">Report a bug</span>
        </button>
        <Menu
          label={
            <span aria-label={doc.privacy === 'private' ? 'Private' : doc.privacy === 'unlisted' ? 'Unlisted' : 'Public'}>
              {doc.privacy === 'private' ? '🔒' : doc.privacy === 'unlisted' ? '🔗' : '🌐'}
              <span className="wide-only"> {doc.privacy === 'private' ? 'Private' : doc.privacy === 'unlisted' ? 'Unlisted' : 'Public'}</span> ▾
            </span>
          }
          className="privacy"
        >
          <MenuItem onClick={() => void setPrivacy('private')}>🔒 Private — only you</MenuItem>
          <MenuItem onClick={() => void setPrivacy('unlisted')}>🔗 Unlisted — anyone with the link</MenuItem>
          <MenuItem onClick={() => void setPrivacy('public')}>🌐 Public</MenuItem>
          {doc.slug && (
            <MenuItem
              onClick={() => {
                void navigator.clipboard?.writeText(`${location.origin}/s/${doc.slug}`);
              }}
            >
              Copy link
            </MenuItem>
          )}
        </Menu>
        <Menu label={user ? (user.guest ? 'Guest' : user.name) : 'Sign in'}>
          {user && !user.guest ? (
            <>
              <div className="menu-label">{user.email}</div>
              <MenuItem onClick={() => openModal('login', { mode: 'logout' })}>Sign out</MenuItem>
            </>
          ) : (
            <>
              {user?.guest && <div className="menu-label">You’re saving as a guest.</div>}
              <MenuItem onClick={() => openModal('login', { mode: 'signup' })}>Create account</MenuItem>
              <MenuItem onClick={() => openModal('login', { mode: 'login' })}>Sign in</MenuItem>
            </>
          )}
        </Menu>
        <button className={`save-btn${clean ? ' saved' : ''}`} onClick={() => void save()} disabled={doc.status === 'saving'} {...commandTip('app.save')} data-testid="save">
          <Icon icon={{ svg: 'M5 4h11l3 3v13H5zM8 4v5h7V4M8 20v-6h8v6' }} size={16} />
          <span className={doc.canEdit ? 'wide-only' : undefined}>{doc.canEdit ? (doc.status === 'saving' ? 'Saving…' : 'Save') : 'Duplicate to edit'}</span>
        </button>
      </div>
    </header>
  );
}
